
import { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CookieBanner } from '@/components/CookieBanner';
import { KonamiEasterEgg } from '@/components/KonamiEasterEgg';

// Pages
const Index = lazy(() => import('./pages/Index'));
const Pricing = lazy(() => import('./pages/Pricing'));
const NotFound = lazy(() => import('./pages/NotFound'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const UserDashboard = lazy(() => import('./pages/user/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const ProfilePage = lazy(() => import('./pages/user/Profile'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const LogoutConfirmation = lazy(() => import('./pages/LogoutConfirmation'));
const PaymentRequired = lazy(() => import('./pages/PaymentRequired'));
const CompanyManagementPage = lazy(() => import('./pages/user/CompanyManagement'));
const RegistrationComplete = lazy(() => import('./pages/RegistrationComplete'));
const CompleteRegistration = lazy(() => import('./pages/CompleteRegistration'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Careers = lazy(() => import('./pages/Careers'));

// Admin pages
const PlatformList = lazy(() => import('./pages/admin/platforms/PlatformList'));
const PlatformDetail = lazy(() => import('./pages/admin/platforms/PlatformDetail'));
const PlatformForm = lazy(() => import('./pages/admin/platforms/PlatformForm'));
const ScoringWeightsPage = lazy(() => import('./pages/admin/platforms/ScoringWeightsPage'));

const UserManagement = lazy(() => import('./pages/admin/users/UserManagement'));
const ChargingStructureForm = lazy(() => import('./pages/admin/charging/ChargingStructureForm'));
const WrapperList = lazy(() => import('./pages/admin/wrappers/WrapperList'));
const WrapperForm = lazy(() => import('./pages/admin/wrappers/WrapperForm'));
const CompanyRoutes = lazy(() => import('./pages/admin/companies'));
const BackgroundDataPage = lazy(() => import('./pages/admin/background-data/BackgroundDataPage'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));

// User pages
const ClientGroupList = lazy(() => import('./pages/user/clients/ClientGroupList'));
const ClientGroupDetail = lazy(() => import('./pages/user/clients/ClientGroupDetail'));
const ClientDetail = lazy(() => import('./pages/user/clients/ClientDetail'));
const ClientForm = lazy(() => import('./pages/user/clients/ClientForm'));
const ClientGroupForm = lazy(() => import('./pages/user/clients/ClientGroupForm'));
const UserPlatformList = lazy(() => import('./pages/user/platforms/PlatformList'));
const UserPlatformDetail = lazy(() => import('./pages/user/platforms/PlatformDetail'));
const UserSpecialTermsForm = lazy(() => import('./pages/user/platforms/SpecialTermsForm'));
const AdviserChargingPage = lazy(() => import('./pages/user/charging/AdviserChargingPage'));
const PlatformChargeCalculator = lazy(() => import('./pages/user/platforms/PlatformChargeCalculator'));
const PlatformComparisonPage = lazy(() => import('./pages/user/platforms/PlatformComparisonPage'));
const ConsolidationTopUpPage = lazy(() => import('./pages/user/platforms/ConsolidationTopUpPage'));
const PlatformComparisonTool = lazy(() => import('./pages/user/platforms/PlatformComparisonTool'));
const ExistingInvestmentBreakdown = lazy(() => import('./pages/user/platforms/ExistingInvestmentBreakdown'));
const QualitativeComparisonPage = lazy(() => import('./pages/user/platforms/QualitativeComparisonPage'));
const DueDiligencePage = lazy(() => import('./pages/user/platforms/DueDiligencePage'));
const SubscriptionManagement = lazy(() => import('./pages/user/SubscriptionManagement'));

// Cash Management Platforms
const AdminCashPlatformList = lazy(() => import('./pages/admin/cash-platforms/CashPlatformList'));
const AdminCashPlatformForm = lazy(() => import('./pages/admin/cash-platforms/CashPlatformForm'));
const AdminCashPlatformDetail = lazy(() => import('./pages/admin/cash-platforms/CashPlatformDetail'));
const UserCashPlatformList = lazy(() => import('./pages/user/cash-platforms/CashPlatformList'));
const UserCashPlatformDetail = lazy(() => import('./pages/user/cash-platforms/CashPlatformDetail'));
const UserCashPlatformComparison = lazy(() => import('./pages/user/cash-platforms/CashPlatformComparison'));

// Auth
import AuthGuard from './components/AuthGuard';
import ScrollToTop from './components/ScrollToTop';
import SiteFooter from './components/layout/SiteFooter';

// Layout
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const UserLayout = lazy(() => import('./components/layout/UserLayout'));

// New imports for Model Portfolio Managers
const ModelPortfolioProviderList = lazy(() => import('./pages/admin/model-portfolios/ModelPortfolioProviderList'));
const ModelPortfolioProviderForm = lazy(() => import('./pages/admin/model-portfolios/ModelPortfolioProviderForm'));
const ModelPortfolioProviderDetail = lazy(() => import('./pages/admin/model-portfolios/ModelPortfolioProviderDetail'));
const PlatformDiscountsForm = lazy(() => import('./pages/admin/model-portfolios/PlatformDiscountsForm'));
const PortfolioPerformancePage = lazy(() => import('./pages/admin/model-portfolios/PortfolioPerformancePage'));
const PortfolioPerformanceIndexPage = lazy(() => import('./pages/admin/model-portfolios/PortfolioPerformanceIndexPage'));
const PortfolioAllocationPage = lazy(() => import('./pages/admin/model-portfolios/PortfolioAllocationPage'));
const ImportFactsheetsPage = lazy(() => import('./pages/admin/model-portfolios/ImportFactsheetsPage'));
const IaSectorPerformancePage = lazy(() => import('./pages/admin/background-data/IaSectorPerformancePage'));
const BulkIaSectorPerformancePage = lazy(() => import('./pages/admin/background-data/BulkIaSectorPerformancePage'));
const BulkProviderPortfolioPerformancePage = lazy(() => import('./pages/admin/model-portfolios/BulkProviderPortfolioPerformancePage'));

// User Model Portfolio pages
const UserModelPortfolioProviderList = lazy(() => import('./pages/user/model-portfolios/ModelPortfolioProviderList'));
const UserModelPortfolioProviderDetail = lazy(() => import('./pages/user/model-portfolios/ModelPortfolioProviderDetail'));
const ComparePerformancePage = lazy(() => import('./pages/user/model-portfolios/ComparePerformancePage'));
const SavedReportsPage = lazy(() => import('./pages/user/reports/SavedReportsPage'));

// Tax-Advantaged Hub (BR / VCT / EIS)
const AdminTaProviderList = lazy(() => import('./pages/admin/tax-advantaged/TaProviderList'));
const AdminTaProviderForm = lazy(() => import('./pages/admin/tax-advantaged/TaProviderForm'));
const UserTaProviderList = lazy(() => import('./pages/user/tax-advantaged/TaProviderList'));
const UserTaProviderDetail = lazy(() => import('./pages/user/tax-advantaged/TaProviderDetail'));
const UserBrOfferingDetail = lazy(() => import('./pages/user/tax-advantaged/BrOfferingDetail'));
const UserVctOfferingDetail = lazy(() => import('./pages/user/tax-advantaged/VctOfferingDetail'));
const UserEisOfferingDetail = lazy(() => import('./pages/user/tax-advantaged/EisOfferingDetail'));
const UserBrOfferingComparison = lazy(() => import('./pages/user/tax-advantaged/BrOfferingComparison'));
const UserVctOfferingComparison = lazy(() => import('./pages/user/tax-advantaged/VctOfferingComparison'));
const UserEisOfferingComparison = lazy(() => import('./pages/user/tax-advantaged/EisOfferingComparison'));

// Support pages
const ContactUs = lazy(() => import('./pages/user/ContactUs'));
const ContactUsDetail = lazy(() => import('./pages/user/ContactUsDetail'));
const UserGuidePage = lazy(() => import('./pages/user/UserGuidePage'));
const PublicContact = lazy(() => import('./pages/PublicContact'));
const SupportMessages = lazy(() => import('./pages/admin/support/SupportMessages'));
const SupportMessageDetail = lazy(() => import('./pages/admin/support/SupportMessageDetail'));

// Announcements
const AnnouncementManagement = lazy(() => import('./pages/admin/announcements/AnnouncementManagement'));

// Discount Codes
const DiscountCodeList = lazy(() => import('./pages/admin/discount-codes/DiscountCodeList'));
const DiscountCodeForm = lazy(() => import('./pages/admin/discount-codes/DiscountCodeForm'));

// Subscriptions
const AdminSubscriptionManagement = lazy(() => import('./pages/admin/subscriptions/SubscriptionManagement'));

// Manual Approvals
const ManualApprovals = lazy(() => import('./pages/admin/ManualApprovals'));
const AdminDocuments = lazy(() => import('./pages/admin/Documents'));
const QuestionnaireImportsList = lazy(() => import('./pages/admin/questionnaire-imports/QuestionnaireImportsList'));
const QuestionnaireImportReview = lazy(() => import('./pages/admin/questionnaire-imports/QuestionnaireImportReview'));
const QuestionnaireDevFlags = lazy(() => import('./pages/admin/questionnaire-imports/QuestionnaireDevFlags'));
const DocumentReaderPage = lazy(() => import('./pages/admin/document-reader/DocumentReaderPage'));
const ImportHubPage = lazy(() => import('./pages/admin/import-hub/ImportHubPage'));

// Launch Waitlist
const LaunchWaitlist = lazy(() => import('./pages/admin/LaunchWaitlist'));


// Email Management
const EmailManagement = lazy(() => import('./pages/admin/emails/EmailManagement'));

// Registration Pending
const RegistrationPending = lazy(() => import('./pages/RegistrationPending'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));

// Reactivate Account
const ReactivateAccount = lazy(() => import('./pages/ReactivateAccount'));

// Subscription Contact Admin
const SubscriptionContactAdmin = lazy(() => import('./pages/SubscriptionContactAdmin'));

// Auth Modal Context
import { AuthModalProvider } from './contexts/AuthModalContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { AuthProvider } from './hooks/useAuth';
import { IdleTimeoutProvider } from './components/IdleTimeoutProvider';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { SkipToContent } from './components/a11y/SkipToContent';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Avoid disruptive automatic refetches. Data is treated as fresh for
      // 30 minutes; queries only re-run when the user navigates to a page
      // whose cached data is older than that, or when code explicitly
      // invalidates / refetches after a mutation. Tab focus, reconnects and
      // component remounts no longer trigger background reloads that would
      // wipe in-progress form state.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 30 * 60_000,
      gcTime: 60 * 60_000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <ImpersonationProvider>
          <AuthProvider>
          <AccessibilityProvider>
          <IdleTimeoutProvider>
          <AuthModalProvider>
            <SkipToContent />
            <div className="min-h-screen flex flex-col">
            <Suspense fallback={null}>
            <div className="relative z-10 flex-1 flex flex-col bg-background">
            <Routes>
          {/* Public route for landing page */}
          <Route path="/" element={<Index />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment-required" element={<PaymentRequired />} />
          <Route path="/contact" element={<PublicContact />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/careers" element={<Careers />} />
          
          {/* Public auth routes */}
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/logout-confirmation" element={<LogoutConfirmation />} />
          <Route path="/registration/complete" element={<RegistrationComplete />} />
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          
          {/* User routes - All wrapped in UserLayout */}
          <Route path="/user" element={<Navigate to="/user/dashboard" replace />} />
          <Route path="/user/dashboard" element={
            <AuthGuard>
              <UserDashboard />
            </AuthGuard>
          } />
          
          {/* User client routes - All wrapped in UserLayout */}
          <Route path="/user/clients" element={
            <AuthGuard>
              <ClientGroupList />
            </AuthGuard>
          } />
          <Route path="/user/clients/new" element={
            <AuthGuard>
              <ClientGroupForm />
            </AuthGuard>
          } />
          <Route path="/user/clients/groups/:id" element={
            <AuthGuard>
              <ClientGroupDetail />
            </AuthGuard>
          } />
          <Route path="/user/clients/groups/:id/edit" element={
            <AuthGuard>
              <ClientGroupForm />
            </AuthGuard>
          } />
          <Route path="/user/clients/groups/:groupId/client/new" element={
            <AuthGuard>
              <ClientForm />
            </AuthGuard>
          } />
          <Route path="/user/clients/:id" element={
            <AuthGuard>
              <ClientDetail />
            </AuthGuard>
          } />
          <Route path="/user/clients/:id/edit" element={
            <AuthGuard>
              <ClientForm />
            </AuthGuard>
          } />
          <Route path="/user/clients/head/new" element={
            <AuthGuard>
              <ClientForm />
            </AuthGuard>
          } />

          {/* User platform routes */}
          <Route path="/user/platforms" element={
            <AuthGuard>
              <UserPlatformList />
            </AuthGuard>
          } />
          <Route path="/user/platforms/qualitative-comparison" element={
            <AuthGuard>
              <QualitativeComparisonPage />
            </AuthGuard>
          } />
          <Route path="/user/platforms/due-diligence" element={
            <AuthGuard>
              <DueDiligencePage />
            </AuthGuard>
          } />
          <Route path="/user/platforms/:id" element={
            <AuthGuard>
              <UserPlatformDetail />
            </AuthGuard>
          } />
          <Route path="/user/platforms/:id/special-terms/edit" element={
            <AuthGuard>
              <UserSpecialTermsForm />
            </AuthGuard>
          } />
          <Route path="/user/platforms/:id/calculator" element={
            <AuthGuard>
              <PlatformChargeCalculator />
            </AuthGuard>
          } />
          <Route path="/user/platform-comparison/compare/:groupId" element={
            <AuthGuard>
              <PlatformComparisonPage />
            </AuthGuard>
          } />
          <Route path="/user/platform-comparison/consolidation/:groupId" element={
            <AuthGuard>
              <ConsolidationTopUpPage />
            </AuthGuard>
          } />
          <Route path="/user/platform-comparison/existing-breakdown/:groupId" element={
            <AuthGuard>
              <ExistingInvestmentBreakdown />
            </AuthGuard>
          } />
          <Route path="/user/platform-comparison" element={
            <AuthGuard>
              <PlatformComparisonTool />
            </AuthGuard>
          } />
          <Route path="/user/reports" element={
            <AuthGuard>
              <SavedReportsPage />
            </AuthGuard>
          } />
          
          {/* User model portfolio manager routes */}
          <Route path="/user/model-portfolios" element={
            <AuthGuard>
              <UserModelPortfolioProviderList />
            </AuthGuard>
          } />
          <Route path="/user/model-portfolios/:id" element={
            <AuthGuard>
              <UserModelPortfolioProviderDetail />
            </AuthGuard>
          } />

          {/* User cash management platform routes */}
          <Route path="/user/cash-platforms" element={
            <AuthGuard><UserCashPlatformList /></AuthGuard>
          } />
          <Route path="/user/cash-platforms/compare" element={
            <AuthGuard><UserCashPlatformComparison /></AuthGuard>
          } />
          <Route path="/user/cash-platforms/:id" element={
            <AuthGuard><UserCashPlatformDetail /></AuthGuard>
          } />
          
          
          {/* User profile and company management routes */}
          <Route path="/user/profile" element={
            <AuthGuard>
              <ProfilePage />
            </AuthGuard>
          } />
          <Route path="/user/company-management" element={
            <AuthGuard>
              <CompanyManagementPage />
            </AuthGuard>
          } />
          
          {/* User adviser charging route - Updated to use the new page */}
          <Route path="/user/charging" element={
            <AuthGuard>
              <AdviserChargingPage />
            </AuthGuard>
          } />
          
          {/* User contact routes */}
          <Route path="/user/contact" element={
            <AuthGuard>
              <ContactUs />
            </AuthGuard>
          } />
          <Route path="/user/contact/:id" element={
            <AuthGuard>
              <ContactUsDetail />
            </AuthGuard>
          } />

          {/* User guide */}
          <Route path="/user/user-guide" element={
            <AuthGuard>
              <UserGuidePage />
            </AuthGuard>
          } />

          {/* User-wrapped info/legal pages */}
          <Route path="/user/faq" element={
            <AuthGuard>
              <UserLayout><FAQ embedded /></UserLayout>
            </AuthGuard>
          } />
          <Route path="/user/careers" element={
            <AuthGuard>
              <UserLayout><Careers embedded /></UserLayout>
            </AuthGuard>
          } />
          <Route path="/user/terms-and-conditions" element={
            <AuthGuard>
              <UserLayout><TermsAndConditions embedded /></UserLayout>
            </AuthGuard>
          } />
          <Route path="/user/privacy-policy" element={
            <AuthGuard>
              <UserLayout><PrivacyPolicy embedded /></UserLayout>
            </AuthGuard>
          } />
          <Route path="/user/cookie-policy" element={
            <AuthGuard>
              <UserLayout><CookiePolicy embedded /></UserLayout>
            </AuthGuard>
          } />
          
          {/* User subscription route */}
          <Route path="/user/subscription" element={
            <AuthGuard>
              <SubscriptionManagement />
            </AuthGuard>
          } />
          
          {/* Subscription contact admin route */}
          <Route path="/subscription-contact-admin" element={
            <AuthGuard>
              <SubscriptionContactAdmin />
            </AuthGuard>
          } />
          
          {/* Admin routes - All wrapped in AdminLayout */}
          <Route path="/admin" element={
            <AuthGuard adminRequired={true}>
              <AdminDashboard />
            </AuthGuard>
          } />
          <Route path="/admin/dashboard" element={
            <AuthGuard adminRequired={true}>
              <AdminDashboard />
            </AuthGuard>
          } />
          <Route path="/admin/platforms" element={
            <AuthGuard adminRequired={true}>
              <PlatformList />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/scoring-weights" element={
            <AuthGuard adminRequired={true}>
              <ScoringWeightsPage />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/new" element={
            <AuthGuard adminRequired={true}>
              <PlatformForm />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/:id" element={
            <AuthGuard adminRequired={true}>
              <PlatformDetail />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/:id/edit" element={
            <AuthGuard adminRequired={true}>
              <PlatformForm />
            </AuthGuard>
          } />

          {/* Admin cash management platform routes */}
          <Route path="/admin/cash-platforms" element={
            <AuthGuard adminRequired={true}><AdminCashPlatformList /></AuthGuard>
          } />
          <Route path="/admin/cash-platforms/new" element={
            <AuthGuard adminRequired={true}><AdminCashPlatformForm /></AuthGuard>
          } />
          <Route path="/admin/cash-platforms/:id" element={
            <AuthGuard adminRequired={true}><AdminCashPlatformDetail /></AuthGuard>
          } />
          <Route path="/admin/cash-platforms/:id/edit" element={
            <AuthGuard adminRequired={true}><AdminCashPlatformForm /></AuthGuard>
          } />

          


          
          {/* Admin charging structure routes */}
          <Route path="/admin/platforms/:id/charging/new" element={
            <AuthGuard adminRequired={true}>
              <ChargingStructureForm />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/:id/charging/:structureId" element={
            <AuthGuard adminRequired={true}>
              <ChargingStructureForm />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/:id/charging/structure" element={
            <AuthGuard adminRequired={true}>
              <ChargingStructureForm />
            </AuthGuard>
          } />
          <Route path="/admin/platforms/:id/charging/:structureId/edit" element={
            <AuthGuard adminRequired={true}>
              <ChargingStructureForm />
            </AuthGuard>
          } />
          
          <Route path="/admin/wrappers" element={
            <AuthGuard adminRequired={true}>
              <WrapperList />
            </AuthGuard>
          } />
          <Route path="/admin/wrappers/new" element={
            <AuthGuard adminRequired={true}>
              <WrapperForm />
            </AuthGuard>
          } />
          <Route path="/admin/wrappers/:id" element={
            <AuthGuard adminRequired={true}>
              <WrapperForm />
            </AuthGuard>
          } />
          <Route path="/admin/users" element={
            <AuthGuard adminRequired={true}>
              <UserManagement />
            </AuthGuard>
          } />
          <Route path="/admin/audit-log" element={
            <AuthGuard adminRequired={true}>
              <AuditLogPage />
            </AuthGuard>
          } />
          <Route path="/admin/launch-waitlist" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <LaunchWaitlist />
              </AdminLayout>
            </AuthGuard>
          } />
          <Route path="/admin/documents" element={
            <AuthGuard adminRequired={true}>
              <AdminDocuments />
            </AuthGuard>
          } />
          <Route path="/admin/questionnaire-imports" element={
            <AuthGuard adminRequired={true}><QuestionnaireImportsList /></AuthGuard>
          } />
          <Route path="/admin/questionnaire-imports/dev-flags" element={
            <AuthGuard adminRequired={true}><QuestionnaireDevFlags /></AuthGuard>
          } />
          <Route path="/admin/questionnaire-imports/:id" element={
            <AuthGuard adminRequired={true}><QuestionnaireImportReview /></AuthGuard>
          } />
          <Route path="/admin/document-reader" element={
            <AuthGuard adminRequired={true}><DocumentReaderPage /></AuthGuard>
          } />
          <Route path="/admin/import-hub" element={
            <AuthGuard adminRequired={true}><ImportHubPage /></AuthGuard>
          } />
          
          
          {/* Admin company routes */}
          <Route path="/admin/companies/*" element={
            <AuthGuard adminRequired={true}>
              <CompanyRoutes />
            </AuthGuard>
          } />
          
          {/* Background Data routes */}
          <Route path="/admin/background-data" element={
            <AuthGuard adminRequired={true}>
              <BackgroundDataPage />
            </AuthGuard>
          } />
          
          {/* Legacy Logo Cleanup redirect */}
          <Route path="/admin/logo-cleanup" element={
            <Navigate to="/admin/background-data?tab=logo-cleanup" replace />
          } />
          
          {/* Legacy Technology Provider redirects */}
          <Route path="/admin/platforms/technology-providers" element={
            <Navigate to="/admin/background-data" replace />
          } />
          <Route path="/admin/platforms/technology-providers/*" element={
            <Navigate to="/admin/background-data" replace />
          } />
          
          {/* Model Portfolio Manager Routes */}
          <Route path="/admin/model-portfolios" element={
            <AuthGuard adminRequired={true}>
              <ModelPortfolioProviderList />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/import-factsheet" element={
            <AuthGuard adminRequired={true}>
              <ImportFactsheetsPage />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/new" element={
            <AuthGuard adminRequired={true}>
              <ModelPortfolioProviderForm />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/:id" element={
            <AuthGuard adminRequired={true}>
              <ModelPortfolioProviderDetail />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/:id/edit" element={
            <AuthGuard adminRequired={true}>
              <ModelPortfolioProviderForm />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/:providerId/platform-discounts" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <PlatformDiscountsForm />
              </AdminLayout>
            </AuthGuard>
          } />
          <Route path="/admin/portfolio-performance" element={
            <AuthGuard adminRequired={true}>
              <PortfolioPerformanceIndexPage />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/portfolio/:id/performance" element={
            <AuthGuard adminRequired={true}>
              <PortfolioPerformancePage />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/portfolio/:id/allocation" element={
            <AuthGuard adminRequired={true}>
              <PortfolioAllocationPage />
            </AuthGuard>
          } />
          <Route path="/admin/ia-sectors/:id/performance" element={
            <AuthGuard adminRequired={true}>
              <IaSectorPerformancePage />
            </AuthGuard>
          } />
          <Route path="/admin/ia-sectors/bulk-performance" element={
            <AuthGuard adminRequired={true}>
              <BulkIaSectorPerformancePage />
            </AuthGuard>
          } />
          <Route path="/admin/model-portfolios/:providerId/bulk-performance" element={
            <AuthGuard adminRequired={true}>
              <BulkProviderPortfolioPerformancePage />
            </AuthGuard>
          } />

          <Route path="/user/model-portfolios/compare-performance" element={
            <AuthGuard>
              <ComparePerformancePage />
            </AuthGuard>
          } />
          <Route path="/user/model-portfolios/compare" element={
            <AuthGuard>
              <ComparePerformancePage />
            </AuthGuard>
          } />
          

          {/* Tax-Advantaged Hub — admin */}
          <Route path="/admin/tax-advantaged" element={
            <AuthGuard adminRequired={true}><AdminTaProviderList /></AuthGuard>
          } />
          <Route path="/admin/tax-advantaged/new" element={
            <AuthGuard adminRequired={true}><AdminTaProviderForm /></AuthGuard>
          } />
          <Route path="/admin/tax-advantaged/:id" element={
            <AuthGuard adminRequired={true}><AdminTaProviderForm /></AuthGuard>
          } />
          <Route path="/admin/tax-advantaged/:id/edit" element={
            <AuthGuard adminRequired={true}><AdminTaProviderForm /></AuthGuard>
          } />


          {/* Tax-Advantaged Hub — user */}
          <Route path="/user/tax-advantaged" element={
            <AuthGuard><UserTaProviderList /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/business-relief" element={
            <AuthGuard><UserTaProviderList filter="br" /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/vct" element={
            <AuthGuard><UserTaProviderList filter="vct" /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/eis" element={
            <AuthGuard><UserTaProviderList filter="eis" /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/business-relief/compare" element={
            <AuthGuard><UserBrOfferingComparison /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/vct/compare" element={
            <AuthGuard><UserVctOfferingComparison /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/eis/compare" element={
            <AuthGuard><UserEisOfferingComparison /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/providers/:slug" element={
            <AuthGuard><UserTaProviderDetail /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/providers/:providerSlug/br/:offeringSlug" element={
            <AuthGuard><UserBrOfferingDetail /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/providers/:providerSlug/vct/:offeringSlug" element={
            <AuthGuard><UserVctOfferingDetail /></AuthGuard>
          } />
          <Route path="/user/tax-advantaged/providers/:providerSlug/eis/:offeringSlug" element={
            <AuthGuard><UserEisOfferingDetail /></AuthGuard>
          } />
          
          {/* Admin support routes */}
          <Route path="/admin/support" element={
            <AuthGuard adminRequired={true}>
              <SupportMessages />
            </AuthGuard>
          } />
          <Route path="/admin/support/:id" element={
            <AuthGuard adminRequired={true}>
              <SupportMessageDetail />
            </AuthGuard>
          } />
          
          {/* Admin announcements routes */}
          <Route path="/admin/announcements" element={
            <AuthGuard adminRequired={true}>
              <AnnouncementManagement />
            </AuthGuard>
          } />

          {/* Admin email management routes */}
          <Route path="/admin/emails" element={
            <AuthGuard adminRequired={true}>
              <EmailManagement />
            </AuthGuard>
          } />

          {/* Discount Codes */}
          <Route path="/admin/discount-codes" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <DiscountCodeList />
              </AdminLayout>
            </AuthGuard>
          } />
          <Route path="/admin/discount-codes/new" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <DiscountCodeForm />
              </AdminLayout>
            </AuthGuard>
          } />
          <Route path="/admin/discount-codes/:id" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <DiscountCodeForm />
              </AdminLayout>
            </AuthGuard>
          } />

          {/* Subscriptions */}
          <Route path="/admin/subscriptions" element={
            <AuthGuard adminRequired={true}>
              <AdminSubscriptionManagement />
            </AuthGuard>
          } />

          {/* Manual Approvals */}
          <Route path="/admin/manual-approvals" element={
            <AuthGuard adminRequired={true}>
              <AdminLayout>
                <ManualApprovals />
              </AdminLayout>
            </AuthGuard>
          } />


          {/* Registration Pending */}
          <Route path="/registration-pending" element={<RegistrationPending />} />
          
          {/* Reactivate Account */}
          <Route path="/reactivate-account" element={
            <AuthGuard>
              <ReactivateAccount />
            </AuthGuard>
          } />
          
          <Route path="*" element={<NotFound />} />
          </Routes>
          </div>
          </Suspense>
          <SiteFooter />
          </div>
          <Toaster />
          <CookieBanner />
          <KonamiEasterEgg />
        </AuthModalProvider>
        </IdleTimeoutProvider>
        </AccessibilityProvider>
        </AuthProvider>
      </ImpersonationProvider>
    </Router>
    </QueryClientProvider>
  );
}

export default App;
